import { jsx, jsxs } from "react/jsx-runtime";
import { DSFormLayoutBlockItem } from "@elliemae/ds-form-layout-blocks";
import { DSMultiComboBox } from "@elliemae/ds-form-multi-combobox";
import { Grid } from "@elliemae/ds-grid";
import { useMemo, useState, useEffect } from "react";
import { useFormState, useWatch } from "react-hook-form";
import _ from "lodash";
import { DSButtonV3 } from "@elliemae/ds-button-v2";
import { InfoCircle } from "@elliemae/ds-icons";
import { useMultiComboboxFieldUpdateLogic } from "../utils/fieldUpdateLogic";
import { useFieldAutoSetValue } from "../utils/fieldAutoSetValue";
import { useFieldVisibilityAndDisability } from "../utils/fieldVisibilityAndDisability";
import { useLookupOptions } from "../utils/utils";
import { useComboboxOptions } from "../utils/useComboboxOptions";
import { MultipleProductsInfo } from "./multipleProductsInfo";
const ProductsCombobox = ({
  renderedField,
  schemaForm,
  rules,
  id,
  name,
  label,
  required,
  isMulti,
  pathValue,
  feedbackMessage,
  ui,
  options,
  visible,
  disabled,
  disableCheck,
  visibleCheck,
  valueHandler,
  preview
}) => {
  const multiComboboxFieldUpdateLogic = useMultiComboboxFieldUpdateLogic();
  const { isVisible, isDisabled } = useFieldVisibilityAndDisability({
    schemaForm,
    rules,
    visible,
    disabled,
    visibleCheck,
    disableCheck,
    preview
  });
  const { errors } = useFormState({ control: schemaForm.control });
  const errorMessage = _.get(errors, pathValue)?.message;
  const sectionOfAct = schemaForm.getValues("loanInformation.sectionOfAct");
  const { allOptionsTransformed, visibleOptions: allComboboxOptions } = useComboboxOptions({
    options,
    schemaForm,
    rules,
    preview
  });
  const [effectiveValue, setEffectiveValue] = useState(
    schemaForm.getValues(pathValue)
  );
  useEffect(() => {
    setEffectiveValue(schemaForm.getValues(pathValue));
  }, [renderedField?.value]);
  const normalizeOptionValue = (val) => Array.isArray(val) ? val.map(
    (v) => typeof v === "object" && v?.value !== void 0 ? v.value : v
  ) : val;
  useFieldAutoSetValue({
    schemaForm,
    rules,
    pathValue,
    valueHandler,
    preview,
    normalizeValue: normalizeOptionValue,
    onAutoSet: setEffectiveValue
  });
  const [specialProducts, standardProducts] = useWatch({
    control: schemaForm.control,
    name: ["specialProducts", "standardProducts"]
  });
  const mapValueToLookup = (values, lookups) => (Array.isArray(values) ? values : []).map(
    (val) => (lookups ?? []).find((look) => look.value === val)?.label
  );
  const standardProductLookups = useLookupOptions("standardProducts");
  const specialProductLookups = useLookupOptions("specialProducts");
  const selectedStandardProducts = useMemo(
    () => mapValueToLookup(standardProducts, standardProductLookups),
    [standardProductLookups, standardProducts]
  );
  const selectedSpecialProducts = useMemo(
    () => mapValueToLookup(specialProducts, specialProductLookups),
    [specialProductLookups, specialProducts]
  );
  const allSelectedProducts = useMemo(() => {
    return `${selectedStandardProducts.join(
      ", "
    )}, ${selectedSpecialProducts.join(", ")}`;
  }, [selectedStandardProducts, selectedSpecialProducts]);
  const [isMultipleProductsInfoOpen, setIsMultipleProductsInfoOpen] = useState(false);
  const MultipleProductsWarning = useMemo(() => {
    const conditions = {
      "Special Products": () => specialProducts?.length >= 2 || specialProducts?.length && standardProducts?.length,
      "Standard Products": () => standardProducts?.length >= 2 || standardProducts?.length && specialProducts?.length
    };
    const shouldShowWarning = conditions[name]?.();
    if (shouldShowWarning) {
      return /* @__PURE__ */ jsx(
        Grid,
        {
          mx: "xxs",
          style: {
            paddingBottom: feedbackMessage ? "16px" : "0px"
          },
          children: /* @__PURE__ */ jsx(
            DSButtonV3,
            {
              buttonType: "iconFilled",
              "aria-label": "Multiple Products Info",
              onClick: () => setIsMultipleProductsInfoOpen(true),
              children: /* @__PURE__ */ jsx(InfoCircle, {})
            }
          )
        }
      );
    }
    return null;
  }, [standardProducts, specialProducts]);
  return /* @__PURE__ */ jsxs(Grid, { span: ui.col_size, style: { display: isVisible ? "block" : "none" }, children: [
    /* @__PURE__ */ jsx(
      MultipleProductsInfo,
      {
        isModalOpen: isMultipleProductsInfoOpen,
        handleCancel: () => setIsMultipleProductsInfoOpen(false),
        selectedPrograms: allSelectedProducts
      }
    ),
    /* @__PURE__ */ jsxs(Grid, { cols: ["auto", "min-content"], alignItems: "flex-end", children: [
      /* @__PURE__ */ jsx(
        DSFormLayoutBlockItem,
        {
          name,
          label,
          inputID: name,
          required,
          feedbackMessage,
          hasError: !!errors[id] || !!errorMessage,
          validationMessage: errors[id]?.message?.toString() || errorMessage,
          children: /* @__PURE__ */ jsx(
            DSMultiComboBox,
            {
              ...renderedField,
              allOptions: allComboboxOptions,
              selectedValues: Array.isArray(effectiveValue) ? effectiveValue.map(
                (val) => allOptionsTransformed.find(
                  (option) => option.value === val.toString()
                )
              ).filter(Boolean) : [],
              onChange: multiComboboxFieldUpdateLogic(
                renderedField,
                schemaForm,
                id
              ),
              id: pathValue,
              disabled: isDisabled
            }
          )
        }
      ),
      MultipleProductsWarning
    ] })
  ] });
};
export {
  ProductsCombobox
};
